import { useStaticQuery, graphql } from "gatsby"
import { useAllSitePageData } from "./use-all-site-page-data";

export type NavTreeFields = {
  id: string;
  label: string | null;
  position: number | null;
  link: Queries.NavigationLinkFieldsFragment | null;
  treeChildren: ReadonlyArray<NavTreeFields> | null;
};

export type MainNavLogoData = {
  logo: string;
  title: string;
  path: string;
};

export type MainNavData = {
  tabName: string;
  navLogo: MainNavLogoData;
  navTree: ReadonlyArray<NavTreeFields> | null;
};

export type NavigationSectionMap = {
  wsfecNavigation: MainNavData;
  stateFairNavigation: MainNavData;
  oktoberfestNavigation: MainNavData;
  holidayMagicNavigation: MainNavData;
  springFairNavigation: MainNavData;
  tasteNwLanding: MainNavData;
  foundationNavigation: MainNavData;
};

export const useSiteSectionNavigationData = () => {
  const allSitePageData = useAllSitePageData();
  const { datoCmsGlobalTabNavigation, allDatoCmsNavigationNode } = useStaticQuery<Queries.NavigationDataQuery>(
    graphql`
      query NavigationData {
        datoCmsGlobalTabNavigation {
          ...GlobalTabNavigationFields
        }
        allDatoCmsNavigationNode(filter: {root: {eq: true}}, sort: {position: ASC}) {
          edges {
            node {
              ...NavigationNodeFields
              treeChildren {
                ...NavigationNodeFields
                treeChildren {
                  ...NavigationNodeFields
                  treeChildren {
                    ...NavigationNodeFields
                    treeChildren {
                      ...NavigationNodeFields
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
  );
  
  /**
   * Gets the child navigation tree of the given target NavigationNode id. Used to get the navigation trees to be used
   * in each section of the site, based on the root NavigationNode specified in the GlobalTabNavigation single instance model.
   * 
   * NOTE: This function doesn't ensure that the entire navigation tree is properly sorted, so it must be sorted using the position
   * field available in each node.
   * 
   * @param data An array of Navigation Node models to check for the target node in
   * @param targetId The id of a Navigation node you want to find the children of
   * 
   * @returns An array of the children of the target Navigation Node, or null if the target wasn't found
   */
  const getChildNavigationNodes = (data: ReadonlyArray<NavTreeFields>, targetId: string): ReadonlyArray<NavTreeFields> | null => {
    const queue: NavTreeFields[] = [];
    queue.push(...data);
  
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) {
        continue;
      }
  
      if (current.id === targetId) {
        return current.treeChildren ?? null;
      }
  
      if (current.treeChildren) {
        queue.push(...current.treeChildren);
      }
    }
  
    return null;
  };

  /**
   * Gets the path for the given Dato page model instance id
   * 
   * @param sectionLandingPageId The id of the landing page for the section
   * @returns string The path to the page for this section's landing page (or '/' if one wasn't found)
   */
  const getPathForNavSectionLogo = (sectionLandingPageId: string) => {
    const page = allSitePageData.find(page => page.pageContext?.id === sectionLandingPageId);
    return page?.path ?? '/';
  };

  const navNodes = allDatoCmsNavigationNode.edges.map(({node: navNode}) => navNode);
  const sectionMap: NavigationSectionMap = {
    wsfecNavigation: {
      tabName: 'wsfec',
      navLogo: {
        logo: 'full-logo-wsfec.svg',
        title: 'Washington State Fair Events Center',
        // This is a special case where we can safely hardcode the path /
        path: '/'
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.wsfecNavigationNode?.id ?? ''),
    },
    stateFairNavigation: {
      tabName: 'wsf',
      navLogo: {
        logo: 'full-logo-wsf.svg',
        title: 'Washington State Fair',
        path: getPathForNavSectionLogo(datoCmsGlobalTabNavigation?.stateFairLandingPage?.id ?? ''),
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.stateFairNavigationNode?.id ?? ''),
    },
    oktoberfestNavigation: {
      tabName: 'oktoberfest',
      navLogo: {
        logo: 'full-logo-oktoberfest.svg',
        title: 'Oktoberfest NW',
        path: getPathForNavSectionLogo(datoCmsGlobalTabNavigation?.oktoberfestLandingPage?.id ?? ''),
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.oktoberfestNavigationNode?.id ?? ''),
    },
    holidayMagicNavigation: {
      tabName: 'holiday-magic',
      navLogo: {
        logo: 'full-logo-holiday-magic.svg',
        title: 'Holiday Magic',
        path: getPathForNavSectionLogo(datoCmsGlobalTabNavigation?.holidayMagicLandingPage?.id ?? ''),
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.holidayMagicNavigationNode?.id ?? ''),
    },
    springFairNavigation: {
      tabName: 'wsf-spring',
      navLogo: {
        logo: 'full-logo-wsf-spring.svg',
        title: 'Washington State Spring Fair',
        path: getPathForNavSectionLogo(datoCmsGlobalTabNavigation?.springFairLandingPage?.id ?? ''),
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.springFairNavigationNode?.id ?? ''),
    },
    tasteNwLanding: {
      tabName: 'taste',
      navLogo: {
        logo: 'full-logo-taste.svg',
        title: 'The Taste NW',
        path: getPathForNavSectionLogo(datoCmsGlobalTabNavigation?.tasteNwLandingPage?.id ?? ''),
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.tasteNwNavigationNode?.id ?? ''),
    },
    foundationNavigation: {
      tabName: 'foundation',
      navLogo: {
        logo: 'full-logo-foundation.svg',
        title: 'Washington State Fair Foundation',
        path: getPathForNavSectionLogo(datoCmsGlobalTabNavigation?.foundationLandingPage?.id ?? ''),
      },
      navTree: getChildNavigationNodes(navNodes as NavTreeFields[], datoCmsGlobalTabNavigation?.foundationNavigationNode?.id ?? ''),
    },
  };

  return sectionMap;
}